/**
 * Single Shared API Client
 * Used by all signup forms for geolocation and lead submission
 */

// API Endpoints
const GEOLOCATION_API = 'https://trademarklawassociates.com/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://trademarklawassociates.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * Returns country code, IP address, city, state
 * @returns {Promise<Object|null>} Geolocation data or null on error
 */
export async function getGeoLocation() {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
}

/**
 * Submit lead data to the API
 * @param {Object} payload - Complete payload object ready for submission
 * Required fields: name, email, countryDialCode, country, phoneNumber, ipAddress, description, pageUrl
 * Optional fields: city, state, geoLocation, packageTitle, packageAmount, tmSignUpType
 * @returns {Promise<Object>} API response with success status and data/message
 */
export async function submitLead(payload) {
  try {
    // Submit to Lead API
    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle response according to status code
    // 200 Success - redirect to application page
    if (data.statusCode === 200 && data.data?.customerId) {
      return {
        success: true,
        statusCode: 200,
        data: data.data,
        message: data.data.message || 'Success',
        customerId: data.data.customerId
      };
    } 
    // 401 Unauthorized - show message field
    else if (data.statusCode === 401) {
      return {
        success: false,
        statusCode: 401,
        message: data.message || 'Unauthorized'
      };
    } 
    // 400 Bad Request - show errorMessage field
    else if (data.statusCode === 400) {
      return {
        success: false,
        statusCode: 400,
        message: data.errorMessage || 'Bad Request'
      };
    } 
    // Other errors
    else {
      return {
        success: false,
        statusCode: data.statusCode || 500,
        message: data.errorMessage || data.message || 'An error occurred'
      };
    }

  } catch (error) {
    console.error('Error submitting lead:', error);
    return {
      success: false,
      statusCode: 500,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Helper function to build lead payload from form data and geolocation
 * @param {Object} formData - Form values from user input
 * @param {Object} geoData - Geolocation data from getGeoLocation()
 * @returns {Object} Complete payload ready for submitLead()
 */
export function buildLeadPayload(formData, geoData) {
  // Clean phone number - numeric only (no +, spaces, or special characters)
  const cleanPhoneNumber = (formData.phoneNumber || '').replace(/\D/g, '');
  
  // Build base payload with required fields
  const payload = {
    name: formData.name,
    email: formData.email,
    countryDialCode: parseInt(formData.countryDialCode) || 1,
    country: geoData?.data?.countryCode || formData.country || 'US',
    phoneNumber: cleanPhoneNumber,
    ipAddress: geoData?.data?.ipAddress || '',
    description: formData.description || '',
    pageUrl: formData.pageUrl || window.location.href
  };

  // Add optional fields only if they exist (don't send empty strings or placeholders)
  if (geoData?.data?.cityName) {
    payload.city = geoData.data.cityName;
  }
  
  if (geoData?.data?.regionName) {
    payload.state = geoData.data.regionName;
  }
  
  if (geoData) {
    payload.geoLocation = JSON.stringify(geoData);
  }
  
  if (formData.packageTitle) {
    payload.packageTitle = formData.packageTitle;
  }
  
  if (formData.packageAmount) {
    payload.packageAmount = Number(formData.packageAmount) || 0;
  }

  if (formData.tmSignUpType) {
    payload.tmSignUpType = Number(formData.tmSignUpType) || 0;
  }

  return payload;
}

/**
 * Get country code from dial code
 * Common country codes mapping
 */
export const getCountryCodeFromDialCode = (dialCode) => {
  const countryMap = {
    '1': 'US',
    '44': 'GB',
    '91': 'IN',
    '92': 'PK',
    '93': 'AF',
    '355': 'AL',
    '213': 'DZ',
    '376': 'AD',
    '244': 'AO',
    '54': 'AR',
    '374': 'AM',
    '61': 'AU',
    '43': 'AT',
    '994': 'AZ',
    '973': 'BH',
    '880': 'BD',
    '375': 'BY',
    '32': 'BE',
    '501': 'BZ',
    '229': 'BJ',
    '975': 'BT',
    '591': 'BO',
    '387': 'BA',
    '267': 'BW',
    '55': 'BR',
    '673': 'BN',
    '359': 'BG',
    '226': 'BF',
    '257': 'BI',
    '855': 'KH',
    '237': 'CM',
    '238': 'CV',
    '236': 'CF',
    '235': 'TD',
    '56': 'CL',
    '86': 'CN',
    '57': 'CO',
    '269': 'KM',
    '242': 'CG',
    '506': 'CR',
    '385': 'HR',
    '53': 'CU',
    '357': 'CY',
    '420': 'CZ',
    '45': 'DK',
    '253': 'DJ',
    '593': 'EC',
    '20': 'EG',
    '503': 'SV',
    '240': 'GQ',
    '291': 'ER',
    '372': 'EE',
    '251': 'ET',
    '679': 'FJ',
    '358': 'FI',
    '33': 'FR',
    '241': 'GA',
    '220': 'GM',
    '995': 'GE',
    '49': 'DE',
    '233': 'GH',
    '30': 'GR',
    '502': 'GT',
    '224': 'GN',
    '245': 'GW',
    '592': 'GY',
    '509': 'HT',
    '504': 'HN',
    '852': 'HK',
    '36': 'HU',
    '354': 'IS',
    '62': 'ID',
    '98': 'IR',
    '964': 'IQ',
    '353': 'IE',
    '972': 'IL',
    '39': 'IT',
    '81': 'JP',
    '962': 'JO',
    '7': 'KZ',
    '254': 'KE',
    '686': 'KI',
    '965': 'KW',
    '996': 'KG',
    '856': 'LA',
    '371': 'LV',
    '961': 'LB',
    '266': 'LS',
    '231': 'LR',
    '218': 'LY',
    '423': 'LI',
    '370': 'LT',
    '352': 'LU',
    '853': 'MO',
    '389': 'MK',
    '261': 'MG',
    '265': 'MW',
    '60': 'MY',
    '960': 'MV',
    '223': 'ML',
    '356': 'MT',
    '692': 'MH',
    '222': 'MR',
    '230': 'MU',
    '52': 'MX',
    '691': 'FM',
    '373': 'MD',
    '377': 'MC',
    '976': 'MN',
    '382': 'ME',
    '212': 'MA',
    '258': 'MZ',
    '95': 'MM',
    '264': 'NA',
    '674': 'NR',
    '977': 'NP',
    '31': 'NL',
    '64': 'NZ',
    '505': 'NI',
    '227': 'NE',
    '234': 'NG',
    '850': 'KP',
    '47': 'NO',
    '968': 'OM',
    '92': 'PK',
    '680': 'PW',
    '507': 'PA',
    '675': 'PG',
    '595': 'PY',
    '51': 'PE',
    '63': 'PH',
    '48': 'PL',
    '351': 'PT',
    '974': 'QA',
    '40': 'RO',
    '7': 'RU',
    '250': 'RW',
    '966': 'SA',
    '221': 'SN',
    '381': 'RS',
    '248': 'SC',
    '232': 'SL',
    '65': 'SG',
    '421': 'SK',
    '386': 'SI',
    '677': 'SB',
    '252': 'SO',
    '27': 'ZA',
    '82': 'KR',
    '211': 'SS',
    '34': 'ES',
    '94': 'LK',
    '249': 'SD',
    '597': 'SR',
    '268': 'SZ',
    '46': 'SE',
    '41': 'CH',
    '963': 'SY',
    '886': 'TW',
    '992': 'TJ',
    '255': 'TZ',
    '66': 'TH',
    '228': 'TG',
    '676': 'TO',
    '216': 'TN',
    '90': 'TR',
    '993': 'TM',
    '688': 'TV',
    '256': 'UG',
    '380': 'UA',
    '971': 'AE',
    '598': 'UY',
    '998': 'UZ',
    '678': 'VU',
    '58': 'VE',
    '84': 'VN',
    '967': 'YE',
    '260': 'ZM',
    '263': 'ZW'
  };
  
  return countryMap[dialCode] || 'US';
};
